import type { BBox, LonLat } from "../../api/types";
import type { Basemap } from "../../state/ui";

/** Fond de carte d'une zone libre (docs/14 §2.7, point 10 ; §6.1) : hors de l'emprise IGN (France métropolitaine et
 *  départements d'outre-mer), le fond Mapbox est imposé sans écraser la préférence mémorisée ; les fonds IGN sont
 *  alors grisés dans le panneau des calques. En mode MapLibre (sans jeton Mapbox), la zone libre est désactivée. */

/** Emprises couvertes par les fonds IGN (Géoplateforme) : métropole et Corse, Guadeloupe, Martinique, Guyane, La
 *  Réunion, Mayotte. */
export const IGN_COVERAGE: readonly BBox[] = [
  [-5.6, 41.2, 9.8, 51.3],
  [-61.9, 15.8, -60.9, 16.6],
  [-61.3, 14.3, -60.7, 14.95],
  [-54.7, 2.1, -51.5, 5.9],
  [55.1, -21.5, 55.95, -20.8],
  [44.9, -13.1, 45.4, -12.55],
];

export function inIgnCoverage([lon, lat]: LonLat): boolean {
  return IGN_COVERAGE.some(([w, s, e, n]) => lon >= w && lon <= e && lat >= s && lat <= n);
}

/** Milieu d'une emprise. */
export const bboxCenter = ([w, s, e, n]: BBox): LonLat => [(w + e) / 2, (s + n) / 2];

/** Fond imposé par la zone affichée : Mapbox pour une zone libre hors de l'emprise IGN (moteur Mapbox), sinon aucun. */
export function forcedBasemap(options: {
  engine: "mapbox" | "maplibre";
  kind: "camp" | "user" | undefined;
  bbox: BBox | null | undefined;
}): Basemap | null {
  if (options.engine !== "mapbox" || options.kind !== "user" || !options.bbox) return null;
  return inIgnCoverage(bboxCenter(options.bbox)) ? null : "mapbox";
}

/** Fond effectivement affiché : le fond imposé, sinon la préférence (le plan IGN remplace Mapbox sans jeton). */
export function effectiveBasemap(engine: "mapbox" | "maplibre", basemap: Basemap, forced: Basemap | null): Basemap {
  if (forced) return forced;
  return engine === "maplibre" && basemap === "mapbox" ? "ign-plan" : basemap;
}
